'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import PracticeAreaCard from './PracticeAreaCard';
import FAQAccordion from './FAQAccordion';
import PracticeAreaFilter from './PracticeAreaFilter';
import ConsultationCTA from './ConsultationCTA';

interface CaseType {
  title: string;
  description: string;
}

interface Benefit {
  title: string;
  description: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface CaseStudy {
  title: string;
  description: string;
  result: string;
}

interface PracticeArea {
  id: string;
  icon: string;
  title: string;
  description: string;
  caseTypes: CaseType[];
  benefits: Benefit[];
  faqs: FAQ[];
  timeline: string;
  feeStructure: string;
  caseStudy: CaseStudy;
  ctaText: string;
}

const PracticeAreasInteractive: React.FC = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const practiceAreas: PracticeArea[] = [
    {
      id: 'probate',
      icon: 'DocumentTextIcon',
      title: 'Probate & Estate Litigation',
      description: 'Navigate complex estate disputes with clarity and confidence. Our experienced probate litigation attorneys, estate litigation attorneys, and will contest lawyers transform contentious probate matters into resolved outcomes, protecting your inheritance rights and family legacy.',
      caseTypes: [
        {
          title: 'Probate Litigation',
          description: 'Experienced probate litigation attorneys handling complex estate administration disputes, executor challenges, and beneficiary rights protection.',
        },
        {
          title: 'Estate Litigation',
          description: 'Skilled estate litigation attorneys resolving trust disputes, asset distribution conflicts, and fiduciary duty breaches.',
        },
        {
          title: 'Will Contest',
          description: 'Expert will contest lawyers challenging or defending wills based on undue influence, lack of capacity, fraud, or improper execution.',
        },
        {
          title: 'Inheritance Claims',
          description: 'Protect rightful inheritance through claims against improper distributions or hidden assets.',
        },
      ],
      benefits: [
        {
          title: 'Strategic Estate Resolution',
          description: 'We develop customized litigation strategies that prioritize your goals while minimizing family conflict.',
        },
        {
          title: 'Fiduciary Duty Expertise',
          description: 'Deep understanding of executor and trustee obligations ensures accountability and proper estate administration.',
        },
        {
          title: 'Asset Protection Focus',
          description: 'Aggressive representation to secure and preserve estate assets throughout the litigation process.',
        },
        {
          title: 'Compassionate Advocacy',
          description: 'We understand the emotional complexity of family disputes and provide empathetic, professional guidance.',
        },
      ],
      faqs: [
        {
          question: 'How long does probate litigation typically take?',
          answer: 'Probate litigation timelines vary based on case complexity, but most disputes resolve within 12-24 months. Simple will contests may conclude in 6-12 months, while complex trust disputes involving multiple parties can extend to 2-3 years. We work efficiently to expedite resolution while protecting your interests.',
        },
        {
          question: 'What are grounds for contesting a will?',
          answer: 'Valid grounds include lack of testamentary capacity (mental competence), undue influence by another party, fraud or forgery, improper execution (not meeting legal requirements), and revocation. We thoroughly evaluate your situation to determine if sufficient grounds exist for a successful challenge.',
        },
        {
          question: 'Can I remove an executor or trustee?',
          answer: 'Yes, executors and trustees can be removed for breach of fiduciary duty, mismanagement of assets, conflicts of interest, failure to account properly, or inability to perform duties. We help document misconduct and petition the court for removal when necessary.',
        },
        {
          question: 'What if I was left out of a will?',
          answer: 'Being excluded from a will does not automatically provide grounds for legal action. However, if you can demonstrate undue influence, lack of capacity, or fraud, you may have a valid claim. We assess your specific circumstances to determine available legal remedies.',
        },
      ],
      timeline: 'Most probate litigation cases resolve within 12-24 months, depending on complexity and court schedules.',
      feeStructure: 'We offer flexible fee arrangements including hourly billing and hybrid contingency options for qualified cases. Initial consultation fees are credited toward representation.',
      caseStudy: {
        title: 'Estate Recovery Success',
        description: 'Represented beneficiaries in a complex trust dispute involving $2.3M in mismanaged assets. Through detailed forensic accounting and strategic litigation, we uncovered trustee misconduct and improper distributions.',
        result: 'Secured full recovery of misappropriated funds plus damages, resulting in $2.8M total recovery for our clients.',
      },
      ctaText: 'Schedule Estate Litigation Consultation',
    },
    {
      id: 'personal-injury',
      icon: 'HeartIcon',
      title: 'Personal Injury Law',
      description: 'When injury disrupts your life, we fight for the compensation you deserve. Our comprehensive personal injury practice covers automobile accidents, trucking accidents, wrongful death, product liability, and more. We hold negligent parties accountable for your suffering.',
      caseTypes: [
        {
          title: 'Automobile Accidents',
          description: 'Car accident cases involving negligent drivers, distracted driving, DUI collisions, and multi-vehicle crashes.',
        },
        {
          title: 'Trucking Accidents',
          description: 'Commercial truck accident litigation against trucking companies, addressing driver fatigue, improper maintenance, and federal regulation violations.',
        },
        {
          title: 'Motorcycle Accidents',
          description: 'Motorcycle collision cases addressing severe injuries, bias against riders, and complex liability issues.',
        },
        {
          title: 'Pedestrian Accidents',
          description: 'Pedestrian injury cases involving crosswalk accidents, sidewalk incidents, and driver negligence.',
        },
        {
          title: 'Bicycle Accidents',
          description: 'Cyclist injury claims addressing road hazards, driver negligence, and severe injury compensation.',
        },
        {
          title: 'Boating Accidents',
          description: 'Maritime injury cases involving operator negligence, equipment failure, and waterway accidents.',
        },
        {
          title: 'Slip & Fall Accidents',
          description: 'Premises liability claims for injuries caused by hazardous conditions, inadequate maintenance, or property owner negligence.',
        },
        {
          title: 'Premises Liability',
          description: 'Property owner negligence claims including inadequate security, dangerous conditions, and failure to warn.',
        },
        {
          title: 'Animal & Dog Bites',
          description: 'Dog bite and animal attack cases addressing owner liability, severe injuries, and permanent scarring.',
        },
        {
          title: 'Medical Malpractice',
          description: 'Healthcare negligence cases including surgical errors, misdiagnosis, medication mistakes, and substandard care.',
        },
        {
          title: 'Nursing Home Abuse',
          description: 'Elder abuse and neglect cases protecting vulnerable seniors from inadequate care, physical abuse, or financial exploitation.',
        },
        {
          title: 'Product Liability',
          description: 'Defective product cases involving design defects, manufacturing errors, and inadequate safety warnings.',
        },
        {
          title: 'Wrongful Death',
          description: 'Fatal accident cases providing justice for surviving family members and compensation for loss of companionship, income, and support.',
        },
        {
          title: 'Insurance Bad Faith',
          description: 'Hold insurance companies accountable when they unreasonably deny, delay, or undervalue legitimate personal injury claims.',
        },
      ],
      benefits: [
        {
          title: 'Comprehensive Case Handling',
          description: 'From automobile accidents to complex medical malpractice, we handle all aspects of your personal injury claim with expertise.',
        },
        {
          title: 'Maximum Compensation Focus',
          description: 'We aggressively pursue full compensation for medical expenses, lost wages, pain and suffering, and future damages across all case types.',
        },
        {
          title: 'No Win, No Fee',
          description: 'Contingency fee structure means you pay nothing unless we recover compensation for your injuries, regardless of case type.',
        },
        {
          title: 'Proven Track Record',
          description: 'Decades of experience with automobile accidents, trucking collisions, premises liability, medical malpractice, and wrongful death cases.',
        },
      ],
      faqs: [
        {
          question: 'What types of personal injury cases do you handle?',
          answer: 'We handle all major personal injury cases including automobile accidents, trucking accidents, motorcycle collisions, pedestrian and bicycle accidents, slip & fall cases, premises liability, dog bites, medical malpractice, nursing home abuse, product liability, wrongful death, and insurance bad faith claims. Our comprehensive approach ensures expert representation regardless of how you were injured.',
        },
        {
          question: 'How much is my personal injury case worth?',
          answer: 'Case value depends on injury severity, medical expenses, lost income, pain and suffering, and liability strength. Each case type has unique valuation factors - trucking accidents often involve higher damages than simple car accidents due to injury severity. We provide honest assessments after reviewing your specific circumstances.',
        },
        {
          question: 'How long do I have to file a personal injury claim?',
          answer: 'Most personal injury claims must be filed within 2-3 years from the injury date, though statutes of limitation vary by state and case type. Medical malpractice and wrongful death claims may have different deadlines. Contact us immediately to preserve your rights and avoid missing critical filing deadlines.',
        },
        {
          question: 'What if multiple parties are responsible for my injuries?',
          answer: 'Many personal injury cases involve multiple liable parties - trucking accidents may involve the driver, trucking company, and maintenance provider. Premises liability cases may involve property owners, managers, and contractors. We identify all responsible parties to maximize your compensation from all available sources.',
        },
      ],
      timeline: 'Personal injury cases typically resolve within 6-18 months, though complex cases involving severe injuries, trucking accidents, or medical malpractice may take 2-3 years.',
      feeStructure: 'Contingency fee basis - you pay no attorney fees unless we recover compensation. Fees are a percentage of the recovery amount, with no upfront costs for any personal injury case type.',
      caseStudy: {
        title: 'Multi-Vehicle Trucking Accident Victory',
        description: 'Represented multiple victims in a catastrophic trucking accident involving driver fatigue and improper maintenance. The trucking company initially offered minimal settlements citing shared fault among vehicles.',
        result: 'Secured combined $5.8M settlement for all clients, including compensation for permanent disabilities, lost earning capacity, and pain and suffering.',
      },
      ctaText: 'Get Your Free Injury Case Evaluation',
    },
    {
      id: 'insurance',
      icon: 'ShieldCheckIcon',
      title: 'Insurance Law',
      description: 'Insurance companies profit by denying valid claims. We level the playing field, forcing insurers to honor their contractual obligations and pay what you are rightfully owed.',
      caseTypes: [
        {
          title: 'Claim Denials',
          description: 'Challenge wrongful denials of property, health, disability, and life insurance claims.',
        },
        {
          title: 'Bad Faith Insurance',
          description: 'Hold insurers accountable for unreasonable delays, inadequate investigations, and improper claim handling.',
        },
        {
          title: 'Underinsured Claims',
          description: 'Pursue additional compensation when at-fault parties lack sufficient insurance coverage.',
        },
        {
          title: 'Policy Disputes',
          description: 'Resolve coverage interpretation conflicts and policy exclusion disagreements.',
        },
      ],
      benefits: [
        {
          title: 'Insurance Industry Knowledge',
          description: 'Deep understanding of insurance company tactics, policy language, and claim evaluation processes.',
        },
        {
          title: 'Bad Faith Expertise',
          description: 'Proven track record holding insurers accountable for unreasonable claim denials and delays.',
        },
        {
          title: 'Policy Analysis Precision',
          description: 'Meticulous review of policy language to identify coverage and challenge improper exclusions.',
        },
        {
          title: 'Aggressive Negotiation',
          description: 'We do not accept lowball offers - we fight for full policy benefits plus bad faith damages when applicable.',
        },
      ],
      faqs: [
        {
          question: 'What is bad faith insurance?',
          answer: 'Bad faith occurs when an insurance company unreasonably denies a valid claim, delays payment without justification, fails to properly investigate, or offers substantially less than claim value. Insurers have a legal duty to act in good faith - violations can result in additional damages beyond policy limits.',
        },
        {
          question: 'Can I sue my own insurance company?',
          answer: 'Yes, you can sue your insurer for wrongful claim denial, bad faith practices, or breach of contract. Your insurance policy is a contract - when the company fails to honor its obligations, you have legal recourse. We help policyholders hold their insurers accountable.',
        },
        {
          question: 'How long does an insurance dispute take to resolve?',
          answer: 'Simple claim disputes may resolve in 3-6 months through negotiation. Complex bad faith cases involving litigation typically take 12-24 months. We work efficiently while ensuring thorough case preparation for maximum recovery.',
        },
        {
          question: 'What if my claim was denied due to a policy exclusion?',
          answer: 'Policy exclusions are often misapplied or ambiguous. We carefully analyze policy language, claim circumstances, and applicable law to challenge improper exclusion applications. Many denied claims are actually covered when policies are properly interpreted.',
        },
      ],
      timeline: 'Insurance disputes typically resolve within 6-18 months, with straightforward denials settling faster than complex bad faith litigation.',
      feeStructure: 'Contingency fee arrangements available for most insurance disputes. We advance case costs and only collect fees when we recover compensation for you.',
      caseStudy: {
        title: 'Bad Faith Insurance Victory',
        description: 'Represented a homeowner whose $180,000 fire damage claim was denied based on a questionable policy exclusion. The insurer refused to reconsider despite clear coverage.',
        result: 'Secured $425,000 settlement including full claim value plus bad faith damages and attorney fees.',
      },
      ctaText: 'Fight Your Insurance Claim Denial',
    },
    {
      id: 'contracts',
      icon: 'DocumentCheckIcon',
      title: 'Contracts & Business Agreements',
      description: 'Business relationships depend on clear, enforceable agreements. We draft, review, and litigate contracts to protect your business interests and resolve disputes efficiently.',
      caseTypes: [
        {
          title: 'Contract Drafting',
          description: 'Create comprehensive agreements that protect your interests and minimize future disputes.',
        },
        {
          title: 'Breach of Contract',
          description: 'Enforce agreements when parties fail to perform or seek remedies for breaches against you.',
        },
        {
          title: 'Partnership Disputes',
          description: 'Resolve conflicts between business partners, shareholders, and LLC members.',
        },
        {
          title: 'Vendor & Supplier Issues',
          description: 'Address non-performance, quality disputes, and payment conflicts with business partners.',
        },
      ],
      benefits: [
        {
          title: 'Business-Focused Solutions',
          description: 'We understand business realities and prioritize solutions that protect relationships while enforcing rights.',
        },
        {
          title: 'Preventive Legal Strategy',
          description: 'Proactive contract drafting and review prevents disputes before they arise, saving time and money.',
        },
        {
          title: 'Efficient Dispute Resolution',
          description: 'We pursue cost-effective resolutions through negotiation, mediation, or litigation as circumstances require.',
        },
        {
          title: 'Industry Experience',
          description: 'Broad experience across industries ensures we understand your specific business context and challenges.',
        },
      ],
      faqs: [
        {
          question: 'Do I need a lawyer to review every business contract?',
          answer: 'While not legally required, attorney review of significant contracts is highly recommended. We identify unfavorable terms, ambiguous language, and missing protections that could cost you substantially. The modest review cost prevents expensive disputes and protects your business interests.',
        },
        {
          question: 'What remedies are available for breach of contract?',
          answer: 'Remedies include monetary damages (compensating for losses), specific performance (forcing contract completion), contract rescission (canceling the agreement), and injunctive relief (preventing harmful actions). The appropriate remedy depends on contract terms, breach nature, and your business objectives.',
        },
        {
          question: 'How long do contract disputes take to resolve?',
          answer: 'Resolution timelines vary widely. Simple payment disputes may settle in 2-4 months, while complex partnership dissolutions can take 12-24 months. We pursue efficient resolution while protecting your interests throughout the process.',
        },
        {
          question: 'Can I get out of a bad business contract?',
          answer: 'Possibly, depending on contract terms and circumstances. Valid grounds for termination include material breach by the other party, fraud or misrepresentation, impossibility of performance, or mutual agreement. We analyze your contract and situation to identify available exit strategies.',
        },
      ],
      timeline: 'Contract disputes typically resolve within 6-12 months, with straightforward breaches settling faster than complex partnership dissolutions.',
      feeStructure: 'Hourly billing for contract drafting and review. Litigation matters may qualify for alternative fee arrangements including flat fees or contingency structures.',
      caseStudy: {
        title: 'Partnership Dispute Resolution',
        description: 'Represented a minority shareholder in a business partnership dispute involving breach of fiduciary duty and improper profit distributions. The majority partners refused negotiation.',
        result: 'Secured $890,000 settlement plus buyout of client\'s shares at fair market value, enabling clean business exit.',
      },
      ctaText: 'Schedule Business Law Consultation',
    },
  ];

  const mockPracticeAreas = [
    {
      id: 'all',
      icon: 'ViewGridIcon',
      title: 'All Practice Areas',
      description: 'Comprehensive legal services across multiple practice areas',
      caseTypes: [],
      benefits: [],
      faqs: [],
      timeline: '',
      feeStructure: '',
      caseStudy: {
        title: 'General Practice',
        description: 'General legal services across all practice areas',
        result: 'Comprehensive legal representation and guidance',
      },
      ctaText: 'Explore All Practice Areas',
    },
  ];

  const filterOptions = [
    { id: 'all', label: 'All Practice Areas', icon: 'ViewGridIcon' },
    { id: 'probate', label: 'Probate & Estate', icon: 'DocumentTextIcon' },
    { id: 'personal-injury', label: 'Personal Injury', icon: 'HeartIcon' },
    { id: 'insurance', label: 'Insurance Law', icon: 'ShieldCheckIcon' },
    { id: 'contracts', label: 'Business Contracts', icon: 'DocumentCheckIcon' },
  ];

  const filteredAreas =
    activeFilter === 'all'
      ? practiceAreas
      : practiceAreas.filter((area) => area.id === activeFilter);

  const handleConsultation = () => {
    if (isHydrated) {
      router.push('/contact');
    }
  };

  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-surface">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-border rounded w-3/4"></div>
            <div className="h-6 bg-border rounded w-1/2"></div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="h-96 bg-border rounded"></div>
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-border rounded"></div>
                <div className="h-64 bg-border rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filter Sidebar */}
          <div className="lg:col-span-1">
            <PracticeAreaFilter
              options={filterOptions}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Practice Areas Content */}
          <div className="lg:col-span-3 space-y-12">
            {filteredAreas.map((area) => (
              <div key={area.id} className="space-y-8">
                <PracticeAreaCard
                  icon={area.icon}
                  title={area.title}
                  description={area.description}
                  caseTypes={area.caseTypes}
                  benefits={area.benefits}
                  faqs={area.faqs}
                  timeline={area.timeline}
                  feeStructure={area.feeStructure}
                  caseStudy={area.caseStudy}
                  ctaText={area.ctaText}
                  onCtaClick={handleConsultation}
                />
                <FAQAccordion faqs={area.faqs} practiceArea={area.title} />
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section with ID for scrolling */}
        <section id="faq-section" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-charcoal mb-4 text-center">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-slate-600 text-center mb-12">
                Get answers to common questions about our practice areas and legal services
              </p>
              
              <FAQAccordion 
                faqs={filteredAreas.length > 0 ? filteredAreas[0].faqs : mockPracticeAreas[0].faqs}
              />
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="mt-16">
          <ConsultationCTA
            title="Ready to Discuss Your Legal Matter?"
            description="Schedule a confidential consultation to explore how our expertise can help resolve your legal challenges. We provide honest assessments and strategic guidance tailored to your specific situation."
            buttonText="Schedule Your Free Consultation"
            onButtonClick={handleConsultation}
          />
        </div>
      </div>
    </div>
  );
};

export default PracticeAreasInteractive;